import { useEffect } from 'react';
import { CLIENT_CONFIG, CURRENT_CLIENT } from '@/constants';
import {
  applyFlatThemeColorsToRoot,
  flatThemeColorsToDarkCss,
  MANAGED_THEME_COLOR_CSS_VARS,
} from '@/lib/apply-theme-colors';
import { flattenThemeColors, resolveThemeModeFlat } from '@/lib/theme-colors-normalize';
import { mergeTheme, type ClientTheme } from '@/types/theme';

/**
 * Hook to apply client-specific theme CSS variables to the document root.
 *
 * Behavior:
 * - Light mode: applies client theme colors (merged with defaults)
 * - Dark mode with client dark.colors: applies both light and dark colors
 * - Dark mode without client dark.colors: removes inline colors, lets CSS .dark handle it
 */
export function useClientTheme() {
  useEffect(() => {
    const clientTheme = (CURRENT_CLIENT as { theme?: ClientTheme }).theme;

    if (!clientTheme) {
      return;
    }

    const theme = mergeTheme({
      ...clientTheme,
      colors: flattenThemeColors(clientTheme.colors as Record<string, unknown>) ?? clientTheme.colors,
      dark: clientTheme.dark?.colors
        ? {
            ...clientTheme.dark,
            colors:
              flattenThemeColors(clientTheme.dark.colors as Record<string, unknown>, {
                baseColors: clientTheme.colors as Record<string, unknown>,
              }) ?? clientTheme.dark.colors,
          }
        : clientTheme.dark,
    });
    const root = document.documentElement;
    const clientHasDarkColors = !!clientTheme.dark?.colors;

    const applyThemeColors = () => {
      const isDarkMode = root.classList.contains('dark');
      const shouldApplyLightColors = !isDarkMode;

      if (shouldApplyLightColors && theme.colors) {
        applyFlatThemeColorsToRoot(root, theme.colors);
      } else {
        MANAGED_THEME_COLOR_CSS_VARS.forEach((cssVar) => root.style.removeProperty(cssVar));
      }
    };

    applyThemeColors();

    const observer = new MutationObserver(applyThemeColors);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    if (theme.typography?.fontFamily?.sans) {
      root.style.setProperty('--font-sans', theme.typography.fontFamily.sans);
      document.body.style.fontFamily = theme.typography.fontFamily.sans;
    }
    if (theme.typography?.fontFamily?.brand) {
      root.style.setProperty('--font-brand', theme.typography.fontFamily.brand);
    }

    if (theme.borderRadius?.base) {
      const base = theme.borderRadius.base;
      root.style.setProperty('--radius', base);
      const isFlat = base === '0' || base === '0rem' || base === '0px';
      if (isFlat) {
        root.style.setProperty('--radius-sm', '0');
        root.style.setProperty('--radius-md', '0');
        root.style.setProperty('--radius-lg', '0');
        root.style.setProperty('--radius-xl', '0');
      }
    }

    const buttonRadius = theme.borderRadius?.button ?? '0';
    root.style.setProperty('--radius-button', buttonRadius);

    let darkStyleElement: HTMLStyleElement | null = null;
    if (clientHasDarkColors && clientTheme.dark?.colors) {
      const darkColors = resolveThemeModeFlat(
        {
          colors: clientTheme.colors as Record<string, unknown>,
          dark: { colors: clientTheme.dark.colors as Record<string, unknown> },
        },
        'dark',
      );
      if (darkColors) {
        darkStyleElement = document.getElementById('client-theme-dark') as HTMLStyleElement;
        if (!darkStyleElement) {
          darkStyleElement = document.createElement('style');
          darkStyleElement.id = 'client-theme-dark';
          document.head.appendChild(darkStyleElement);
        }
        darkStyleElement.textContent = flatThemeColorsToDarkCss(darkColors, {
          clientId: CLIENT_CONFIG,
        });
        document.head.appendChild(darkStyleElement);
      }
    } else {
      const existingDarkStyle = document.getElementById('client-theme-dark');
      if (existingDarkStyle) {
        existingDarkStyle.remove();
      }
    }

    return () => {
      observer.disconnect();
      if (darkStyleElement) darkStyleElement.remove();
    };
  }, []);
}
